/**
 * GadsExport.js — Self-Service-Export für EINE Kampagne (oben gewählt): Wochen × Berichte (b00–b16) → ZIP.
 * Die Kampagnen-Auswahl ganz oben gilt für ALLES: Berichte, Fragen (inkl. b13/b14), Was-wurde-gemacht.
 * Backend: api/gads_export_bxx.php (ZIP) + gads_kpi_questions.php (Fragen) + gads_kpi_changelog.php (Log). Bearer.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './GadsKpi.css';

const API_BASE = '/api';
const KAMPS = [['VA', 'Verlustangst'], ['EF', 'Eifersucht'], ['EA', 'Emot. Abh.'], ['SO', 'Souveränität'], ['BR', 'Branding']];
// [key_intern (Daten-Tabelle) · anzeige (neue Nummerierung b0–b16) · label]
const REPORTS = [
  ['b00', 'b00', 'Änderungen'],
  ['b01', 'b01', 'Kampagne'], ['b02', 'b02', 'Auktionen'], ['b03', 'b03', 'Geräte'], ['b04', 'b04', 'Alter'], ['b05', 'b05', 'Standorte'],
  ['b06', 'b06', 'Werbezeitplan'], ['b07', 'b07', 'Zielgruppen'], ['b08', 'b08', 'Suchbegriffe'], ['b09', 'b09', 'Keywords'],
  ['b10', 'b10', 'Titel'], ['b11', 'b11', 'Texte'], ['b12', 'b12', 'Sitelinks'], ['b13', 'b13', 'Marke'], ['b14', 'b14', 'Bilder'],
  ['b15', 'b15', 'Zusammenfassung: K & LP'], ['b16', 'b16', 'User-Intent & eF-Profil'],
];
// Fragen-Liste = Daten-Berichte + Synthese-Fragen (b15/b16, keine Daten-Tabelle, nur Fragen)
const FRAGEN = REPORTS;
// Key == Anzeige == kanonische Speiche (b00–b16, seit Migration 2026-07 vereinheitlicht) → Identität
const DISP2INTERN = Object.fromEntries(REPORTS.map(([intern, disp]) => [disp, intern]));
const SHORT2NAME = { VA: 'SEA_Verlustangst', EF: 'SEA_Eifersucht', EA: 'SEA_Emotionale_Abhaengigkeit', SO: 'SEA_Souveraenitaet', BR: 'SEA_Branding' };
const LP_FIELDS = [['url', 'URL'], ['fs1', 'FS1'], ['fs2', 'FS2'], ['fs3', 'FS3'], ['fs4', 'FS4'], ['titel', 'Titel'], ['meta', 'Meta'], ['woerter', 'Anzahl Wörter'], ['dichte', 'FS-Dichte'], ['index_status', 'Index'], ['stand', 'Stand']];

export default function GadsExport() {
  const [kCamp, setKCamp] = useState('VA'); // EINE Kampagne, gilt für die ganze Seite
  const [weeks, setWeeks] = useState(null);
  const [selWeeks, setSelWeeks] = useState({});
  const [selR, setSelR] = useState(Object.fromEntries(REPORTS.map(r => [r[0], true])));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  // Fragen
  const [frText, setFrText] = useState({});
  const [selFr, setSelFr] = useState(Object.fromEntries(FRAGEN.map(r => [r[0], true])));
  const [frBusy, setFrBusy] = useState(false);
  const [frMsg, setFrMsg] = useState('');
  const [bigTa, setBigTa] = useState({}); // bxx → vergrößert (Doppelklick)
  // Was wurde gemacht (K × Woche)
  const [clWeek, setClWeek] = useState('');
  const [clData, setClData] = useState({ lp_name: '', k_gads: [], k_manual: '', lp: '', source: 'auto' });
  const [clHbPrev, setClHbPrev] = useState({ K: [], LP: [] }); // Vorwochen-Umsetzung je Plan (K/LP) → „Was wurde gemacht"
  const [clBusy, setClBusy] = useState(false);
  const [clMsg, setClMsg] = useState('');
  const [inclCl, setInclCl] = useState(false); // Abschnitt 5 (K-Änderungen) entfernt → Änderungslog ist jetzt b0
  const [inclCdhvK, setInclCdhvK] = useState(true); // CD-HV-K.csv (Abschnitt 3)
  const [inclCdhvLP, setInclCdhvLP] = useState(true); // CD-HV-LP.csv (Abschnitt 4)
  const [inclJourney, setInclJourney] = useState(true); // Journey+Profil ins ZIP (LP-Wirkung)
  const [inclLpMeta, setInclLpMeta] = useState(true); // LP-Stammdaten ins ZIP
  // Journey/Profil-Vorschau (was geht an CD)
  const [jrPrev, setJrPrev] = useState(null); // {sekHead, sekBody, sekN, profHead, profBody, profN}
  const [jrBusy, setJrBusy] = useState(false);
  // LP-Stammdaten (Header der lpxx.md, editierbar)
  const [lpRows, setLpRows] = useState([]);
  const [lpBusy, setLpBusy] = useState(false);
  const [lpMsg, setLpMsg] = useState('');
  // Bereich 7 — CD-Import (Schnittstelle Außen→System): CDs HP-K/HP-LP per Copy-Paste → E-Einträge in ads_hb_umsetzung
  const [hpRaw, setHpRaw] = useState('');
  const [hpPrev, setHpPrev] = useState(null);
  const [hpBusy, setHpBusy] = useState(false);
  const [hpMsg, setHpMsg] = useState('');
  // Bereich 9 = Handlungsplan K · Bereich 10 = Handlungsplan LP (je Plan: content + abhakbare Anweisungen)
  const [hb, setHb] = useState({ K: { content: '', checked: [], struktur: [], edit: false, busy: false, msg: '' }, LP: { content: '', checked: [], struktur: [], edit: false, busy: false, msg: '' } });
  const patchHb = (typ, patch) => setHb(s => ({ ...s, [typ]: { ...s[typ], ...patch } }));
  const [clBig, setClBig] = useState({}); // 'km' | 'lp' → vergrößert
  // Änderungsverlauf-Import (CSV-Datei wählen → Vorschau → Speichern)
  const [impOpen, setImpOpen] = useState(false);
  const [impRaw, setImpRaw] = useState('');
  const [impFile, setImpFile] = useState('');
  const [impPrev, setImpPrev] = useState(null);
  const [impBusy, setImpBusy] = useState(false);
  const [impMsg, setImpMsg] = useState('');

  const token = () => localStorage.getItem('token') || '';

  useEffect(() => {
    fetch(`${API_BASE}/gads_export_bxx.php?list=weeks`, { headers: { Authorization: 'Bearer ' + token() } })
      .then(r => r.json())
      .then(j => { if (j.success) { setWeeks(j.weeks); setSelWeeks(Object.fromEntries(j.weeks.slice(0, 2).map(w => [w.from, true]))); setClWeek(j.weeks[0]?.from || ''); } else setErr(j.message || 'Fehler'); })
      .catch(e => setErr(String(e)));
  }, []);

  const loadChangelog = useCallback((camp, week) => {
    if (!week) return;
    setClMsg('');
    fetch(`${API_BASE}/gads_kpi_changelog.php?campaign=${camp}&week=${week}`, { headers: { Authorization: 'Bearer ' + token() } })
      .then(r => r.json())
      .then(j => { if (j.success) setClData({ lp_name: j.lp_name || '', k_gads: j.k_gads || [], k_manual: j.k_manual || '', lp: j.lp || '', source: j.source || 'auto' }); })
      .catch(() => {});
    // Vorwochen-Umsetzung je Plan (K/LP) — SAUBER aus ads_hb_umsetzung (zeile + erledigt_am), kein rohes checked
    const prev = new Date(new Date(week).getTime() - 7 * 864e5).toISOString().slice(0, 10);
    setClHbPrev({ K: [], LP: [] });
    ['K', 'LP'].forEach(typ => {
      fetch(`${API_BASE}/gads_hb.php?campaign=${camp}&week=${prev}&typ=${typ}&umsetzung=1`, { headers: { Authorization: 'Bearer ' + token() } })
        .then(r => r.json()).then(j => { setClHbPrev(s => ({ ...s, [typ]: j.success ? (j.umsetzung || []) : [] })); }).catch(() => {});
    });
  }, []);
  useEffect(() => { loadChangelog(kCamp, clWeek); }, [kCamp, clWeek, loadChangelog]);
  // „Was wurde gemacht"-Woche innerhalb des oben gewählten Zeitraums halten
  useEffect(() => {
    const sel = Object.keys(selWeeks).filter(k => selWeeks[k]);
    if (sel.length && !sel.includes(clWeek)) setClWeek(sel[0]);
    if (!sel.length && clWeek) setClWeek('');
  }, [selWeeks]); // eslint-disable-line react-hooks/exhaustive-deps

  const impPreview = async (raw) => {
    const csv = (raw != null ? raw : impRaw);
    if (!csv.trim()) return;
    setImpBusy(true); setImpMsg(''); setImpPrev(null);
    try {
      const res = await fetch(`${API_BASE}/gads_kpi_changelog.php`, {
        method: 'POST', headers: { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'preview', campaign: kCamp, csv }),
      });
      const j = await res.json();
      if (j.success) { setImpPrev(j); if (!j.groups.length) setImpMsg('Keine Änderungen erkannt — ist es der GADS-Änderungsverlauf-CSV-Export?'); }
      else setImpMsg('Fehler: ' + (j.message || ''));
    } catch (e) { setImpMsg('Fehler: ' + e); }
    setImpBusy(false);
  };

  const handleImpFile = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = ''; // erlaubt erneutes Wählen derselben Datei
    if (!f) return;
    setImpFile(f.name); setImpMsg(''); setImpPrev(null);
    const reader = new FileReader();
    reader.onload = () => { const text = String(reader.result || ''); setImpRaw(text); impPreview(text); };
    reader.onerror = () => setImpMsg('Datei konnte nicht gelesen werden.');
    reader.readAsText(f);
  };

  const impSave = async () => {
    if (!impPrev || !impPrev.groups.length) return;
    setImpBusy(true); setImpMsg('');
    try {
      const res = await fetch(`${API_BASE}/gads_kpi_changelog.php`, {
        method: 'POST', headers: { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'import', campaign: kCamp, csv: impRaw }),
      });
      const j = await res.json();
      if (j.success) {
        setImpMsg(`✔ ${j.imported} Einträge importiert: ${(j.weeks || []).join(' · ')}`);
        setImpRaw(''); setImpFile(''); setImpPrev(null); loadChangelog(kCamp, clWeek);
      } else setImpMsg('Fehler: ' + (j.message || ''));
    } catch (e) { setImpMsg('Fehler: ' + e); }
    setImpBusy(false);
  };

  const saveChangelog = async () => {
    setClBusy(true); setClMsg('');
    try {
      const res = await fetch(`${API_BASE}/gads_kpi_changelog.php`, {
        method: 'POST', headers: { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaign: kCamp, week: clWeek, k_manual: clData.k_manual, lp: clData.lp }),
      });
      const j = await res.json();
      setClMsg(j.success ? '✔ gespeichert' : ('Fehler: ' + (j.message || '')));
    } catch (e) { setClMsg('Fehler: ' + e); }
    setClBusy(false);
  };

  // LP-Stammdaten (Header der lpxx.md) laden/speichern
  const loadLpMeta = useCallback((camp) => {
    setLpMsg('');
    fetch(`${API_BASE}/gads_lp_meta.php?campaign=${camp}`, { headers: { Authorization: 'Bearer ' + token() } })
      .then(r => r.json()).then(j => { if (j.success) setLpRows(j.rows || []); }).catch(() => {});
  }, []);
  useEffect(() => { loadLpMeta(kCamp); }, [kCamp, loadLpMeta]);

  const setLpField = (i, f, v) => setLpRows(rs => rs.map((r, j) => j === i ? { ...r, [f]: v } : r));

  const saveLpMeta = async () => {
    setLpBusy(true); setLpMsg('');
    try {
      for (const row of lpRows) {
        await fetch(`${API_BASE}/gads_lp_meta.php`, { method: 'POST', headers: { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' }, body: JSON.stringify(row) });
      }
      setLpMsg('✔ gespeichert');
    } catch (e) { setLpMsg('Fehler: ' + e); }
    setLpBusy(false);
  };

  // Bereich 7 — CD-Import: CDs HP-K/HP-LP (Format Typ;Speiche;Empfehlung;Prognose;warum_bx) → E-Einträge
  const hpPreview = async () => {
    if (!hpRaw.trim()) return;
    setHpBusy(true); setHpMsg('');
    try {
      const res = await fetch(`${API_BASE}/gads_hp_import.php`, { method: 'POST', headers: { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'preview', campaign: kCamp, week: clWeek, csv: hpRaw }) });
      const j = await res.json();
      if (j.success) { setHpPrev(j); if (!j.total) setHpMsg('Keine Zeilen erkannt — Format: Typ;Speiche;Empfehlung;Prognose;warum_bx'); }
      else setHpMsg('Fehler: ' + (j.message || ''));
    } catch (e) { setHpMsg('Fehler: ' + e); }
    setHpBusy(false);
  };
  const hpImport = async () => {
    if (!hpPrev || !hpPrev.ok) return;
    setHpBusy(true); setHpMsg('');
    try {
      const res = await fetch(`${API_BASE}/gads_hp_import.php`, { method: 'POST', headers: { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'import', campaign: kCamp, week: clWeek, csv: hpRaw }) });
      const j = await res.json();
      if (j.success) { setHpMsg(`✔ ${j.imported} E-Einträge importiert${j.skipped ? ` · ${j.skipped} übersprungen` : ''} — jetzt im Arbeitsbereich (Bereich 8/9)`); setHpRaw(''); setHpPrev(null); loadBx(kCamp, clWeek, 'K', selCsv); loadBx(kCamp, clWeek, 'LP', selCsv); }
      else setHpMsg('Fehler: ' + (j.message || ''));
    } catch (e) { setHpMsg('Fehler: ' + e); }
    setHpBusy(false);
  };

  // Handlungspläne K + LP laden/speichern/abhaken
  const loadHb = useCallback((camp, week) => {
    if (!week) return;
    ['K', 'LP'].forEach(typ => {
      fetch(`${API_BASE}/gads_hb.php?campaign=${camp}&week=${week}&typ=${typ}`, { headers: { Authorization: 'Bearer ' + token() } })
        .then(r => r.json())
        .then(j => { if (j.success) setHb(s => ({ ...s, [typ]: { ...s[typ], content: j.content || '', checked: j.checked || [], edit: false, msg: '' } })); })
        .catch(() => {});
      fetch(`${API_BASE}/gads_hb.php?campaign=${camp}&week=${week}&typ=${typ}&struktur=1`, { headers: { Authorization: 'Bearer ' + token() } })
        .then(r => r.json())
        .then(j => { if (j.success) setHb(s => ({ ...s, [typ]: { ...s[typ], struktur: j.kategorien || [] } })); })
        .catch(() => {});
    });
  }, []);
  useEffect(() => { loadHb(kCamp, clWeek); }, [kCamp, clWeek, loadHb]);

  // Bereiche 8+9 — Arbeitsansicht je Kategorie (Daten + Entscheidung E/P/V).
  // K: b0–b16 (GADS-Roh-Daten je bx) · LP: a1–a11 (aktueller LP-Section-Inhalt aus ads_lp_sections).
  const [bxData, setBxData] = useState({ K: [], LP: [] });
  const [bxGate, setBxGate] = useState({});
  const [erkLoc, setErkLoc] = useState({});
  const [bxLoc, setBxLoc] = useState({});
  const [bxMsg, setBxMsg] = useState('');
  const [bxSort, setBxSort] = useState({}); // je Tabelle (`${typ}-${kat}`) → { col, dir:1|-1 }
  const bxIsNum = s => s != null && /^-?\d+(\.\d+)?$/.test(String(s).trim());
  const bxCmp = (col, dir) => (a, b) => {
    const x = a[col], y = b[col]; const ex = x == null || x === '', ey = y == null || y === '';
    if (ex && ey) return 0; if (ex) return 1; if (ey) return -1;
    if (bxIsNum(x) && bxIsNum(y)) return (parseFloat(x) - parseFloat(y)) * dir;
    return String(x).localeCompare(String(y), 'de', { numeric: true }) * dir;
  };
  // Die oben angehakten Wochen (period_from) — Datentabellen zeigen ALLE, absteigend (neueste zuerst)
  const selCsv = Object.keys(selWeeks).filter(k => selWeeks[k]).sort().reverse().join(',');
  const loadBx = useCallback((camp, week, typ, weeksCsv) => {
    if (!week) return;
    const wq = weeksCsv ? `&weeks=${encodeURIComponent(weeksCsv)}` : '';
    fetch(`${API_BASE}/gads_hb_bx.php?campaign=${camp}&week=${week}&typ=${typ}${wq}`, { headers: { Authorization: 'Bearer ' + token() } })
      .then(r => r.json()).then(j => { if (j.success) { setBxData(s => ({ ...s, [typ]: j.kategorien || [] })); setBxGate(s => ({ ...s, [typ]: j.gate || null })); } }).catch(() => {});
  }, []);
  useEffect(() => { loadBx(kCamp, clWeek, 'K', selCsv); loadBx(kCamp, clWeek, 'LP', selCsv); setBxLoc({}); setErkLoc({}); }, [kCamp, clWeek, selCsv, loadBx]);
  const saveBxRow = async (typ, kat, nummer, zeile, prognose, erledigt, verdikt, extra = {}) => {
    setBxMsg('⏳ …');
    try {
      await fetch(`${API_BASE}/gads_hb_bx.php`, { method: 'POST', headers: { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' }, body: JSON.stringify({ campaign: kCamp, week: clWeek, typ, kategorie: kat, nummer, zeile, prognose, erledigt, verdikt: verdikt ?? '', ...extra }) });
      setBxMsg('✓ gespeichert'); loadBx(kCamp, clWeek, typ, selCsv); setTimeout(() => setBxMsg(''), 1500);
    } catch { setBxMsg('Fehler'); }
  };
  // bx-Erkenntnis (faktisch/these + gesichtet) — die Ebene VOR dem E.
  const saveErkenntnis = async (typ, kat, faktisch, these, gesichtet) => {
    setBxMsg('⏳ …');
    try {
      await fetch(`${API_BASE}/gads_hb_bx.php`, { method: 'POST', headers: { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' }, body: JSON.stringify({ art: 'erkenntnis', campaign: kCamp, week: clWeek, typ, kategorie: kat, faktisch, these, gesichtet }) });
      setBxMsg('✓ Erkenntnis gespeichert'); loadBx(kCamp, clWeek, typ, selCsv); setTimeout(() => setBxMsg(''), 1500);
    } catch { setBxMsg('Fehler'); }
  };
  // E/P/V-Icon-Badge: Doppelklick vergrößert das Feld, erneuter Doppelklick schließt + speichert.
  const bxBadge = (bigKey, color, letter, onSaveClose) => (
    <span onDoubleClick={() => { const wasBig = !!bigTa[bigKey]; setBigTa(s => ({ ...s, [bigKey]: !s[bigKey] })); if (wasBig) onSaveClose(); }}
      title="Doppelklick = großes Feld / erneut = schließen & speichern"
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: 4, background: color, color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}>{letter}</span>
  );
  // LP-Fragen (a1–a11) direkt hier speichern — persistiert in ads_kpi_questions (nur diesen a-Key)
  const saveBxQuestion = async (aKey) => {
    setBxMsg('⏳ …');
    try {
      await fetch(`${API_BASE}/gads_kpi_questions.php`, { method: 'POST', headers: { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' }, body: JSON.stringify({ campaign: kCamp, questions: { [aKey]: frText[aKey] || '' } }) });
      setBxMsg('✓ Frage gespeichert'); setTimeout(() => setBxMsg(''), 1500);
    } catch { setBxMsg('Fehler'); }
  };

  // Motor: V auf eine offene Vorwochen-Prognose setzen (zielt die WOCHE des Eintrags, nicht clWeek) + Regel-Speicher
  const [rules, setRules] = useState([]);
  const loadRules = useCallback((camp) => {
    fetch(`${API_BASE}/gads_rules.php?campaign=${camp}`, { headers: { Authorization: 'Bearer ' + token() } })
      .then(r => r.json()).then(j => { if (j.success) setRules(j.rules || []); }).catch(() => {});
  }, []);
  useEffect(() => { loadRules(kCamp); }, [kCamp, loadRules]);
  const saveVerdikt = async (typ, e, verdikt) => {
    setBxMsg('⏳ …');
    try {
      await fetch(`${API_BASE}/gads_hb_bx.php`, { method: 'POST', headers: { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' }, body: JSON.stringify({ campaign: kCamp, week: e.woche, typ, kategorie: e.kategorie, nummer: e.nummer, zeile: e.zeile, prognose: e.prognose, verdikt, erledigt: !!e.erledigt_am }) });
      setBxMsg('✓ V gesetzt'); loadBx(kCamp, clWeek, typ, selCsv); setTimeout(() => setBxMsg(''), 1500);
    } catch { setBxMsg('Fehler'); }
  };
  const rulePost = async (body) => {
    try { await fetch(`${API_BASE}/gads_rules.php`, { method: 'POST', headers: { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' }, body: JSON.stringify(body) }); loadRules(kCamp); } catch {}
  };
  const addRule = (typ, kat, bedingung) => rulePost({ action: 'add', campaign: kCamp, typ, kategorie: kat, bedingung });
  const bumpRule = (id) => rulePost({ action: 'bump', id });
  const updateRule = (id, patch) => rulePost({ action: 'update', id, ...patch });
  const deleteRule = (id) => rulePost({ action: 'delete', id });

  const renderBxPlan = (nr, title, typ, hint) => {
    const list = bxData[typ] || [];
    // Akzentfarbe je Plan: K = Indigo · LP = Teal — grenzt die beiden Arbeitsbereiche und die Karten visuell ab
    const accent = typ === 'LP'
      ? { border: '#5eead4', badge: '#0d9488', head: 'linear-gradient(90deg,#ccfbf1,#f0fdfa)', ring: 'rgba(13,148,136,.15)' }
      : { border: '#a5b4fc', badge: '#4f46e5', head: 'linear-gradient(90deg,#e0e7ff,#eef2ff)', ring: 'rgba(79,70,229,.15)' };
    return (
    <section className="gk-section">
      <div className="gk-head" style={{ cursor: 'default' }}>
        <span>{title} <small style={{ fontWeight: 400 }}>({kCamp} × gewählte Zeiträume — {hint})</small></span>
        {bxMsg && <span className="gx-frmsg">{bxMsg}</span>}
      </div>
      <div className="gx-frbody">
        {typ === 'LP' && (
          <div style={{ marginBottom: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontWeight: 600, fontSize: 13 }}>📋 LP-Stammdaten <small style={{ fontWeight: 400 }}>(Basis · read-only — Header der <code>_LPxx.md</code>)</small></span>
              <label className="gx-clincl" style={{ margin: 0 }}><input type="checkbox" checked={inclLpMeta} onChange={() => setInclLpMeta(v => !v)} /> ins ZIP</label>
            </div>
            <div className="gx-frhint">Wahrheit = <code>_LPxx.md</code>-Header (git). <strong>Nur lesbar</strong> — ändern ausschließlich in der MD, dann spiegelt <code>bin/lp-stammdaten-sync</code> es auf den Server (MD → <code>ads_lp_meta</code>).</div>
            {lpRows.length === 0 ? <div className="gk-placeholder">keine LP-Stammdaten für {kCamp} — erst <code>bin/lp-stammdaten-sync</code> laufen lassen.</div> : lpRows.map((r) => (
              <div key={r.lp_id} className="gx-lprow">
                <div className="gx-lpid">{r.lp_id}</div>
                <div className="gx-lpfields">
                  {LP_FIELDS.map(([f, lbl]) => (
                    <div key={f} className="gx-lpfield"><span className="gx-lplbl">{lbl}</span>
                      <span style={{ fontSize: 12, color: '#0f172a', wordBreak: 'break-word' }}>{r[f] == null || r[f] === '' ? '—' : r[f]}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        {typ === 'LP' && (() => {
          const b16 = (bxData.K || []).find(x => x.kat === 'b16');
          const b16q = (frText['b16'] || '').split('\n').map(s => s.trim()).filter(Boolean);
          const b16e = b16 ? b16.eintraege : [];
          return (
            <div style={{ marginBottom: 10, background: '#faf5ff', border: '1px solid #ddd6fe', borderRadius: 6, padding: 8 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>🔎 User-Intent (b16) <small style={{ fontWeight: 400 }}>— Linse für die LP · was wir über den Intent gelernt haben (read-only; Pflege in Arbeitsbereich K · b16)</small></div>
              {b16q.length > 0 && <ol style={{ margin: '2px 0 6px', paddingLeft: 18, fontSize: 12, color: '#6d28d9' }}>{b16q.map((q, i) => <li key={i}>{q}</li>)}</ol>}
              {b16e.length ? b16e.map(e => (
                <div key={e.id} style={{ fontSize: 12, padding: '1px 0' }}><span style={{ color: '#2563eb', fontWeight: 700 }}>E</span> {e.zeile}{e.prognose ? <span style={{ color: '#0891b2' }}> · P: {e.prognose}</span> : ''}{e.verdikt ? <span style={{ color: '#b45309' }}> · V: {e.verdikt}</span> : ''}</div>
              )) : <em style={{ fontSize: 11, opacity: 0.5 }}>(noch keine Intent-Erkenntnis in b16 — im Arbeitsbereich K · b16 setzen)</em>}
              <div className="gx-frhint" style={{ marginTop: 4 }}>Begründe deine a1–a11-Maßnahmen mit dieser Erkenntnis — Feld <code>warum_bx</code> = <code>b16</code> (oder die treibende Speiche).</div>
            </div>
          );
        })()}
        {list.length === 0 ? <span className="gk-placeholder">(lädt …)</span> : list.map(k => {
          const nextNr = k.eintraege.reduce((m, e) => Math.max(m, e.nummer || 0), 0) + 1;
          const nk = `${typ}-${k.kat}-new`; const nl = bxLoc[nk] || {};
          const sk = `${typ}-${k.kat}`; const sort = bxSort[sk];
          const dispRows = sort ? [...(k.rows || [])].sort(bxCmp(sort.col, sort.dir)) : (k.rows || []);
          const grouped = !sort || sort.col === 0; // Wochen-Trennlinie nur sinnvoll bei Default/Sort nach period_from
          const qKey = typ === 'K' ? DISP2INTERN[k.kat] : k.kat; // Fragen-Key (K: intern-Key, LP: a-Key)
          const eKey = `${nk}-E`, pKey = `${nk}-P`, vKey = `${nk}-V`;
          const bigE = !!bigTa[eKey], bigP = !!bigTa[pKey], bigV = !!bigTa[vKey];
          const saveNew = () => {
            const z = (nl.zeile || '').trim(); if (!z) return;
            const pk = (nl.p_kosten || '').trim(), pcpa = (nl.p_cpa || '').trim(), pcv = (nl.p_conv || '').trim();
            const progSummary = [pk && `Kosten: ${pk}`, pcpa && `CPA: ${pcpa}`, pcv && `Conv: ${pcv}`].filter(Boolean).join(' · ') || (nl.prognose || '');
            saveBxRow(typ, k.kat, nextNr, z, progSummary, false, nl.verdikt || '',
              { stuetzt_sich_auf: nl.stuetzt_sich_auf || '', herleitung: nl.herleitung || '', p_kosten: pk, p_cpa: pcpa, p_conv: pcv });
            setBxLoc(s => { const n = { ...s }; delete n[nk]; return n; });
            setBigTa(s => { const n = { ...s }; delete n[eKey]; delete n[pKey]; delete n[vKey]; return n; });
          };
          return (
            <div key={k.kat} style={{ border: `2px solid ${accent.border}`, borderRadius: 10, marginBottom: 16, padding: 10, background: '#fff', boxShadow: `0 1px 4px rgba(30,41,59,.10), 0 0 0 3px ${accent.ring}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '-10px -10px 10px', padding: '7px 10px', background: accent.head, borderBottom: `2px solid ${accent.border}`, borderRadius: '8px 8px 0 0' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 32, height: 22, padding: '0 9px', borderRadius: 11, background: accent.badge, color: '#fff', fontSize: 12, fontWeight: 700 }}>{k.kat}</span>
                <span style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>{k.label}</span>
              </div>
              {k.kat === 'b15' && (() => {
                const all = (bxData[typ] || []).filter(x => x.erkenntnis && ((x.erkenntnis.faktisch || '').trim() || (x.erkenntnis.these || '').trim()));
                const g = bxGate[typ];
                return (
                  <div style={{ margin: '0 0 10px', border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 8, padding: '8px 10px' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>🧭 Synthese — alle bx-Erkenntnisse (Grundlage fürs E)</div>
                    {g && (
                      <div style={{ fontSize: 11, marginBottom: 6, fontWeight: 600, color: g.open ? '#15803d' : '#b45309' }}>
                        {g.open ? '✅ Gate offen — alle Speichen gesichtet, E darf entstehen.' : `🔒 Gate zu — ${g.offen.length} Speiche(n) offen: ${g.offen.join(' · ')}`}
                      </div>
                    )}
                    {all.length === 0 ? <em style={{ fontSize: 11, opacity: 0.6 }}>(noch keine Erkenntnisse — Datenabgleich je bx durchgehen)</em> :
                      all.map(x => (
                        <div key={x.kat} style={{ fontSize: 11, padding: '3px 0', borderTop: '1px solid #eef2f7' }}>
                          <b style={{ color: '#2563eb' }}>{x.kat}</b> <span style={{ color: '#475569' }}>{x.label}</span>
                          {(x.erkenntnis.faktisch || '').trim() && <div style={{ color: '#0f172a' }}>· faktisch: {x.erkenntnis.faktisch}</div>}
                          {(x.erkenntnis.these || '').trim() && <div style={{ color: '#64748b' }}>· these: {x.erkenntnis.these}</div>}
                        </div>
                      ))}
                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 5 }}>E entsteht nur, wenn mehrere bx dieselbe Richtung stützen (und keine stark widerspricht). Das E auf der jeweiligen bx-Karte schreiben (Feld „stützt sich auf") — dort lebt es unter seiner Speiche für den Motor-Check.</div>
                  </div>
                );
              })()}
              <div style={{ marginBottom: 6, background: '#faf5ff', border: '1px solid #ede9fe', borderRadius: 4, padding: '4px 8px' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6d28d9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>❓ Fragen — eine pro Zeile</span>
                  <button className="gk-kbtn" onClick={() => saveBxQuestion(qKey)}>💾</button>
                </div>
                <textarea
                  onDoubleClick={() => { const bk = `${typ}-${qKey}`; const wasBig = !!bigTa[bk]; setBigTa(s => ({ ...s, [bk]: !s[bk] })); if (wasBig) saveBxQuestion(qKey); }}
                  style={{ width: '100%', minHeight: bigTa[`${typ}-${qKey}`] ? 260 : 44, fontSize: 12, marginTop: 2, resize: 'vertical', boxSizing: 'border-box' }}
                  value={frText[qKey] || ''} onChange={e => setFrText(t => ({ ...t, [qKey]: e.target.value }))}
                  placeholder="(noch keine Fragen — hier eintragen; Doppelklick = großes Fenster, erneut = schließen & speichern)" />
              </div>
              {k.geldfluss && (() => {
                const gf = k.geldfluss;
                const isProxy = gf.metric === 'proxy';
                const eur = (n) => (n == null ? '—' : Number(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €');
                const num = (n) => (n == null ? '—' : Number(n).toLocaleString('de-DE'));
                const val = isProxy ? num : eur;
                const pct = (n) => Number(n).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' %';
                const ppf = (n) => (n > 0 ? '+' : '') + Number(n).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                if (gf.metric === 'total') {
                  const dpos = gf.d_total > 0;
                  return (
                    <div style={{ margin: '4px 0 8px', border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 6, padding: '6px 9px' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 3, letterSpacing: '.02em' }}>💶 Wohin floss das Geld? — Gesamt (Vorwoche vs. KW-2)</div>
                      <div style={{ fontSize: 13, color: '#0f172a' }}>
                        <b>{eur(gf.total_vw)}</b> <span style={{ color: '#64748b' }}>(KW-2: {eur(gf.total_vvw)})</span>{' · '}
                        <b style={{ color: dpos ? '#b91c1c' : '#15803d' }}>{dpos ? '+' : ''}{eur(gf.d_total)}</b> vs. KW-2{' · '}
                        {num(gf.conv_vw)} Conv{' · '}CPA {eur(gf.cpa_vw)}
                      </div>
                    </div>
                  );
                }
                const rows = gf.rows || [];
                const shown = rows.slice(0, 12);
                return (
                  <div style={{ margin: '4px 0 8px', border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 6, padding: '6px 8px' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 3, letterSpacing: '.02em' }}>
                      💶 Wohin floss das Geld?{isProxy ? ' — Klick-Anteil (Assets: kein Direkt-€)' : ''} · Vorwoche {val(gf.total_vw)}{isProxy ? ' Klicks' : ''} vs. KW-2 {val(gf.total_vvw)}
                    </div>
                    <div style={{ maxHeight: 190, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 4 }}>
                      <table style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: 11, width: '100%' }}>
                        <thead><tr>{['Segment', isProxy ? 'Klicks' : '€ VW', '%', 'Δ ' + (isProxy ? 'Kl' : '€'), 'Δ pp', 'Conv'].map((h, hi) => (
                          <th key={hi} style={{ position: 'sticky', top: 0, zIndex: 2, background: '#e2e8f0', color: '#0f172a', borderBottom: '2px solid #94a3b8', borderRight: '1px solid #cbd5e1', padding: '2px 6px', textAlign: hi === 0 ? 'left' : 'right', whiteSpace: 'nowrap', fontWeight: 700 }}>{h}</th>
                        ))}</tr></thead>
                        <tbody>
                          {shown.map((r, i) => {
                            const conv = r.conv_vw > 0;
                            const burn = r.vw > 0 && !conv && r.d_eur > 0;
                            const dcol = isProxy ? '#64748b' : (r.d_eur > 0 ? '#b91c1c' : (r.d_eur < 0 ? '#15803d' : '#64748b'));
                            return (
                              <tr key={i} style={{ background: burn ? '#fef2f2' : (conv ? '#f0fdf4' : 'transparent') }}>
                                <td style={{ borderBottom: '1px solid #eef2f7', padding: '2px 6px', fontWeight: 600, color: '#0f172a', whiteSpace: 'normal', maxWidth: 260 }}>{r.seg}</td>
                                <td style={{ borderBottom: '1px solid #eef2f7', padding: '2px 6px', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>{val(r.vw)}</td>
                                <td style={{ borderBottom: '1px solid #eef2f7', padding: '2px 6px', textAlign: 'right' }}>{pct(r.pct_vw)}</td>
                                <td style={{ borderBottom: '1px solid #eef2f7', padding: '2px 6px', textAlign: 'right', whiteSpace: 'nowrap', color: dcol, fontWeight: 600 }}>{r.d_eur > 0 ? '+' : ''}{val(r.d_eur)}</td>
                                <td style={{ borderBottom: '1px solid #eef2f7', padding: '2px 6px', textAlign: 'right', color: '#64748b' }}>{ppf(r.d_pp)}</td>
                                <td style={{ borderBottom: '1px solid #eef2f7', padding: '2px 6px', textAlign: 'right', fontWeight: conv ? 700 : 400, color: conv ? '#15803d' : '#94a3b8' }}>{num(r.conv_vw)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {rows.length > shown.length && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>… {rows.length - shown.length} weitere Segmente (kleinerer Geldfluss)</div>}
                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>🔴 {isProxy ? 'Klicks' : 'Geld'} gewachsen, 0 Conv = Senke · 🟢 hat konvertiert</div>
                  </div>
                );
              })()}
              {k.flow && k.flow.ok && (() => {
                const f = k.flow;
                const eur = (n) => Number(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
                const maxLeak = Math.max(1, ...f.sections.map(s => s.pct_lost), f.converted.pct || 0);
                const bar = (p, col) => (
                  <div style={{ flex: 1, background: '#eef2f7', borderRadius: 3, height: 14, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min(100, (p / maxLeak) * 100)}%`, background: col, height: '100%' }} />
                  </div>
                );
                return (
                  <div style={{ margin: '4px 0 8px', border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 6, padding: '6px 9px' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 2, letterSpacing: '.02em' }}>💶→🛒 Geldfluss × User-Journey · Preis {eur(f.total_vw)} (Vorwoche) → wohin fließt es</div>
                    <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 6 }}>Journey-Form aus {f.journey_days} T ({f.journey_from} → {f.journey_to}) · {f.j_visitors} getrackte Besucher (eingewilligte Teilmenge) · Stichproben-€ {eur(f.eur_sample)} · Ø-CPC {eur(f.cpc_avg)}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', margin: '2px 0 3px' }}>Preis je Suchstring → Journey</div>
                    <div style={{ maxHeight: 170, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 4, marginBottom: 8 }}>
                      <table style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: 11, width: '100%' }}>
                        <thead><tr>{['Suchstring', '€ VW', '%', 'tiefste', 'Ø akt', 'CTA', 'Conv'].map((h, i) => (
                          <th key={i} style={{ position: 'sticky', top: 0, zIndex: 2, background: '#e2e8f0', color: '#0f172a', borderBottom: '2px solid #94a3b8', padding: '2px 6px', textAlign: i === 0 ? 'left' : 'right', whiteSpace: 'nowrap', fontWeight: 700 }}>{h}</th>
                        ))}</tr></thead>
                        <tbody>
                          {f.keywords.map((r, i) => {
                            const conv = r.j_conv > 0; const burn = r.eur_vw > 0 && r.gads_conv === 0;
                            return (
                              <tr key={i} style={{ background: conv ? '#f0fdf4' : (burn ? '#fef2f2' : 'transparent') }}>
                                <td style={{ borderBottom: '1px solid #eef2f7', padding: '2px 6px', fontWeight: 600, whiteSpace: 'normal', maxWidth: 230 }}>{r.kw}</td>
                                <td style={{ borderBottom: '1px solid #eef2f7', padding: '2px 6px', textAlign: 'right', fontWeight: 600 }}>{r.eur_vw > 0 ? eur(r.eur_vw) : '—'}</td>
                                <td style={{ borderBottom: '1px solid #eef2f7', padding: '2px 6px', textAlign: 'right' }}>{r.pct_vw > 0 ? r.pct_vw + ' %' : '—'}</td>
                                <td style={{ borderBottom: '1px solid #eef2f7', padding: '2px 6px', textAlign: 'right' }}>a{r.j_deepest}</td>
                                <td style={{ borderBottom: '1px solid #eef2f7', padding: '2px 6px', textAlign: 'right' }}>{r.j_active}s</td>
                                <td style={{ borderBottom: '1px solid #eef2f7', padding: '2px 6px', textAlign: 'right' }}>{r.j_cta}</td>
                                <td style={{ borderBottom: '1px solid #eef2f7', padding: '2px 6px', textAlign: 'right', fontWeight: conv ? 700 : 400, color: conv ? '#15803d' : '#94a3b8' }}>{r.j_conv}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', margin: '2px 0 3px' }}>a1–a11 · wo das Geld leckt (bezahlter Traffic bricht ab)</div>
                    {f.sections.map((s, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, padding: '1px 0' }}>
                        <span style={{ width: 26, textAlign: 'right', color: '#475569', fontWeight: 600 }}>{s.sec}</span>
                        {bar(s.pct_lost, s.pct_lost >= 15 ? '#dc2626' : (s.pct_lost >= 8 ? '#f59e0b' : '#cbd5e1'))}
                        <span style={{ width: 104, textAlign: 'right', color: '#0f172a', whiteSpace: 'nowrap' }}>{eur(s.eur_lost)} · {s.pct_lost}%</span>
                        <span style={{ width: 40, textAlign: 'right', color: '#94a3b8' }}>{s.visitors} B</span>
                      </div>
                    ))}
                    {f.unknown_sec && f.unknown_sec.visitors > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, padding: '1px 0' }}>
                        <span style={{ width: 26, textAlign: 'right', color: '#94a3b8' }}>?</span>
                        {bar(f.unknown_sec.pct, '#cbd5e1')}
                        <span style={{ width: 104, textAlign: 'right', color: '#64748b', whiteSpace: 'nowrap' }}>{eur(f.unknown_sec.eur)} · {f.unknown_sec.pct}%</span>
                        <span style={{ width: 40, textAlign: 'right', color: '#94a3b8' }}>{f.unknown_sec.visitors} B</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, padding: '2px 0', borderTop: '1px solid #e2e8f0', marginTop: 2 }}>
                      <span style={{ width: 26, textAlign: 'right' }}>✅</span>
                      {bar(f.converted.pct, '#16a34a')}
                      <span style={{ width: 104, textAlign: 'right', color: '#15803d', fontWeight: 700, whiteSpace: 'nowrap' }}>{eur(f.converted.eur)} · {f.converted.pct}%</span>
                      <span style={{ width: 40, textAlign: 'right', color: '#94a3b8' }}>{f.converted.visitors} B</span>
                    </div>
                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>Jeder Besucher trägt den CPC seines Suchstrings; Abbruch bei aX ohne Conv = € an aX verloren. 🔴 ≥15 % · 🟠 ≥8 %. Absolut-€ = Stichprobe, Verteilung = Signal.</div>
                  </div>
                );
              })()}
              {!(k.flow && k.flow.ok) && (<>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', margin: '4px 0 3px', letterSpacing: '.02em' }}>📊 Daten — gewählte Zeiträume</div>
              <div style={{ maxHeight: 240, overflow: 'auto', border: '1px solid #cbd5e1', borderRadius: 4, marginBottom: 6 }}>
                {k.rows && k.rows.length ? (
                  <table style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: 11, width: '100%' }}>
                    <thead>
                      <tr>{(k.cols || []).map((c, ci) => {
                        const active = sort && sort.col === ci;
                        return <th key={c} onClick={() => setBxSort(s => ({ ...s, [sk]: { col: ci, dir: active ? -sort.dir : 1 } }))} title="klicken zum Sortieren" style={{ position: 'sticky', top: 0, zIndex: 2, background: active ? '#cbd5e1' : '#e2e8f0', color: '#0f172a', borderRight: '1px solid #cbd5e1', borderBottom: '2px solid #94a3b8', padding: '3px 6px', textAlign: 'left', whiteSpace: 'nowrap', fontWeight: 700, cursor: 'pointer', userSelect: 'none' }}>{c}{active ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}</th>;
                      })}</tr>
                    </thead>
                    <tbody>
                      {dispRows.map((r, i) => {
                        const newWeek = grouped && i > 0 && dispRows[i - 1] && dispRows[i - 1][0] !== r[0];
                        return (
                          <tr key={i}>
                            {r.map((v, j) => {
                              const long = v != null && String(v).length > 60;
                              return <td key={j} style={{ color: '#0f172a', borderRight: '1px solid #eef2f7', borderBottom: '1px solid #eef2f7', padding: '2px 6px', verticalAlign: 'top', whiteSpace: long ? 'normal' : 'nowrap', ...(long ? { minWidth: 220, maxWidth: 420 } : {}), ...(newWeek ? { borderTop: '2px solid #94a3b8' } : {}), ...(j === 0 ? { fontWeight: 600, background: '#f1f5f9' } : {}) }}>{v == null ? '' : String(v)}</td>;
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : <em style={{ opacity: 0.5, fontSize: 12, padding: '4px 8px', display: 'block' }}>{k.err ? `(Fehler: ${k.err})` : (typ === 'LP' ? '(keine LP-Section-Daten)' : '(keine Daten im Zeitraum)')}</em>}
              </div>
              </>)}
              {k.kat !== 'b15' && (() => {
                const ek = `${typ}-${k.kat}`;
                const base = { faktisch: k.erkenntnis?.faktisch || '', these: k.erkenntnis?.these || '', gesichtet: !!(k.erkenntnis?.gesichtet) };
                const cur = erkLoc[ek] || base;
                const upd = (patch) => setErkLoc(s => ({ ...s, [ek]: { ...cur, ...patch } }));
                const captured = k.erkenntnis && (k.erkenntnis.gesichtet || (k.erkenntnis.faktisch || '').trim() || (k.erkenntnis.these || '').trim());
                return (
                  <div style={{ margin: '8px 0 6px', border: `1px solid ${captured ? '#86efac' : '#cbd5e1'}`, background: captured ? '#f0fdf4' : '#f8fafc', borderRadius: 6, padding: '6px 8px' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 3 }}>🔎 Erkenntnis aus dem Datenabgleich {captured ? '✓' : ''} <small style={{ fontWeight: 400, color: '#94a3b8' }}>— pure Beobachtung, noch KEIN E</small></div>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 3, flexWrap: 'wrap' }}>
                      <input value={cur.faktisch} onChange={e => upd({ faktisch: e.target.value })} onBlur={e => saveErkenntnis(typ, k.kat, e.target.value, cur.these, cur.gesichtet)}
                        placeholder="faktisch — Geld bewegt sich: …" style={{ flex: '1 1 220px', fontSize: 12, padding: '3px 6px', boxSizing: 'border-box' }} />
                      <input value={cur.these} onChange={e => upd({ these: e.target.value })} onBlur={e => saveErkenntnis(typ, k.kat, cur.faktisch, e.target.value, cur.gesichtet)}
                        placeholder="these — Deutung (braucht Stützung anderer bx)" style={{ flex: '1 1 220px', fontSize: 12, padding: '3px 6px', boxSizing: 'border-box' }} />
                    </div>
                    <label style={{ fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                      <input type="checkbox" checked={cur.gesichtet} onChange={e => { upd({ gesichtet: e.target.checked }); saveErkenntnis(typ, k.kat, cur.faktisch, cur.these, e.target.checked); }} />
                      gesichtet — keine Erkenntnis (stille Speiche zählt fürs Gate)
                    </label>
                  </div>
                );
              })()}
              {k.pruef && k.pruef.length > 0 && (
                <div style={{ margin: '8px 0 6px', border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 6, padding: '6px 8px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#b45309', marginBottom: 3 }}>🔄 Vorwoche prüfen — offene Prognosen ({k.pruef.length}) · P ↔ V</div>
                  {k.pruef.map(e => {
                    const umgesetzt = !!e.erledigt_am;
                    return (
                      <div key={e.id} style={{ borderTop: '1px dashed #fde68a', paddingTop: 4, marginTop: 4, fontSize: 12 }}>
                        <div><span style={{ color: '#2563eb', fontWeight: 700 }}>E</span> {e.zeile} <small style={{ color: '#94a3b8' }}>· {e.woche} · {umgesetzt ? '✓ umgesetzt ' + (e.erledigt_am || '').slice(0, 10) : '○ nicht umgesetzt'}</small></div>
                        {e.prognose && <div><span style={{ color: '#0891b2', fontWeight: 700 }}>P</span> <span style={{ color: '#0891b2' }}>{e.prognose}</span></div>}
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 3, alignItems: 'center' }}>
                          {!umgesetzt ? (
                            <button className="gk-kbtn" title="E wurde nicht umgesetzt (b00 belegt es) → E neu definieren" onClick={() => saveVerdikt(typ, e, '○ (c) nicht umgesetzt — E neu')}>○ nicht umgesetzt (c)</button>
                          ) : (<>
                            <button className="gk-kbtn" style={{ borderColor: '#16a34a', color: '#16a34a' }} title="Prognose eingetreten" onClick={() => saveVerdikt(typ, e, '✓ (a) eingetreten (P=V)')}>✓ P=V (a)</button>
                            <span style={{ fontSize: 11, color: '#b91c1c' }}>✗ P≠V (b):</span>
                            <button className="gk-kbtn" style={{ borderColor: '#b91c1c', color: '#b91c1c' }} title="P war zu optimistisch — E war ok, besser prognostizieren" onClick={() => saveVerdikt(typ, e, '✗ P≠V (b-i P falsch kalibriert)')}>i P falsch</button>
                            <button className="gk-kbtn" style={{ borderColor: '#b91c1c', color: '#b91c1c' }} title="Falscher Hebel — Hypothese über die Speiche war falsch" onClick={() => saveVerdikt(typ, e, '✗ P≠V (b-ii falscher Hebel)')}>ii Hebel</button>
                            <button className="gk-kbtn" style={{ borderColor: '#b91c1c', color: '#b91c1c' }} title="Confounder (Google-Automatik/Markt/Saison) — Beleg b00/b02" onClick={() => saveVerdikt(typ, e, '✗ P≠V (b-iii Confounder)')}>iii Confounder</button>
                            <button className="gk-kbtn" title="Noch nicht beurteilbar → P bleibt offen, nächste Woche erneut" onClick={() => setBxMsg('⏳ (d) weiterrollen — P bleibt offen')}>⏳ weiterrollen (d)</button>
                          </>)}
                          <button className="gk-kbtn" style={{ borderColor: '#7c3aed', color: '#7c3aed' }} title="Diesen E als Regel-Kandidat aufnehmen (bei P=V)" onClick={() => addRule(typ, k.kat, e.zeile)}>★ Regel-Kandidat</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', margin: '8px 0 3px', letterSpacing: '.02em', borderTop: '1px solid #f1f5f9', paddingTop: 6 }}>🎯 Entscheidung — <span style={{ color: '#2563eb' }}>E</span> · <span style={{ color: '#0891b2' }}>P</span> · <span style={{ color: '#b45309' }}>V</span></div>
              {k.eintraege.map(e => {
                const key = `${typ}-${k.kat}-${e.nummer}`; const loc = bxLoc[key] || {};
                const erl = loc.erledigt !== undefined ? loc.erledigt : !!e.erledigt_am;
                const zl = loc.zeile !== undefined ? loc.zeile : (e.zeile || '');
                const pr = loc.prognose !== undefined ? loc.prognose : (e.prognose || '');
                const vd = loc.verdikt !== undefined ? loc.verdikt : (e.verdikt || '');
                const eBK = `${key}-E`, pBK = `${key}-P`, vBK = `${key}-V`;
                const save = () => saveBxRow(typ, k.kat, e.nummer, zl, pr, erl, vd);
                const row = (bk, badgeColor, textColor, letter, val, field, ph) => (
                  <div style={{ display: 'flex', gap: 6, alignItems: bigTa[bk] ? 'flex-start' : 'center', marginBottom: 3 }}>
                    {bxBadge(bk, badgeColor, letter, save)}
                    {bigTa[bk]
                      ? <textarea autoFocus style={{ flex: 1, minHeight: 90, fontSize: 12, color: textColor, resize: 'vertical', boxSizing: 'border-box' }} value={val} onChange={ev => setBxLoc(s => ({ ...s, [key]: { ...loc, [field]: ev.target.value } }))} />
                      : <input style={{ flex: 1, fontSize: 12, color: textColor }} value={val} onChange={ev => setBxLoc(s => ({ ...s, [key]: { ...loc, [field]: ev.target.value } }))} placeholder={ph} />}
                  </div>
                );
                return (
                  <div key={e.id} style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 8px', marginBottom: 6, background: '#fafbfc' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5, fontSize: 11 }}>
                      <input type="checkbox" checked={erl} onChange={ev => { const nv = ev.target.checked; setBxLoc(s => ({ ...s, [key]: { ...loc, erledigt: nv } })); saveBxRow(typ, k.kat, e.nummer, zl, pr, nv, vd); }} title="erledigt markieren (Datum/Uhrzeit intern)" />
                      <span style={{ color: erl ? '#16a34a' : '#94a3b8', fontWeight: 600 }}>{erl ? `✓ erledigt${e.erledigt_am ? ' · ' + e.erledigt_am.slice(0, 16) : ''}` : 'offen'}</span>
                      <span style={{ marginLeft: 'auto', color: '#cbd5e1', fontSize: 10 }}>#{e.nummer}</span>
                    </div>
                    {row(eBK, '#2563eb', undefined, 'E', zl, 'zeile', 'Handlungsempfehlung')}
                    {e.stuetzt_sich_auf && <div style={{ fontSize: 10, color: '#94a3b8', margin: '0 0 3px 24px' }}>🔗 stützt sich auf: {e.stuetzt_sich_auf}</div>}
                    {e.herleitung && <div style={{ fontSize: 10, color: '#94a3b8', margin: '0 0 3px 24px', fontStyle: 'italic' }}>💭 {e.herleitung}</div>}
                    {row(pBK, '#0891b2', '#0891b2', 'P', pr, 'prognose', 'Prognose — Metrik + Schwelle (prüfbar in 7 T)')}
                    {row(vBK, '#b45309', '#b45309', 'V', vd, 'verdikt', 'Verdikt (wird i.d.R. nächste Woche gesetzt)')}
                  </div>
                );
              })}
              {(() => {
                const gOpen = !(bxGate[typ]) || bxGate[typ].open;
                const gOffen = (bxGate[typ] && bxGate[typ].offen) || [];
                if (!gOpen) return (
                  <div style={{ border: '1px dashed #cbd5e1', borderRadius: 6, padding: '8px', marginTop: 8, background: '#f8fafc', fontSize: 11, color: '#64748b' }}>
                    🔒 <b>E gesperrt</b> — erst die Erkenntnis-Runde abschließen. Noch offen ({gOffen.length}): {gOffen.join(' · ')}<br />
                    <small style={{ color: '#94a3b8' }}>Ein E entsteht erst aus dem Gesamtbild aller bx (cross-bx-Stützung) — Übersicht auf b15.</small>
                  </div>
                );
                const setNl = (patch) => setBxLoc(s => ({ ...s, [nk]: { ...nl, ...patch } }));
                return (
                  <div style={{ border: '1px dashed #94a3b8', borderRadius: 6, padding: '6px 8px', marginTop: 8, background: '#f8fafc' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 5 }}>➕ neue Empfehlung (E) <small style={{ fontWeight: 400 }}>— cross-bx gestützt · P in 3 Erwartungen</small></div>
                    <div style={{ display: 'flex', gap: 6, alignItems: bigE ? 'flex-start' : 'center', marginBottom: 4 }}>
                      {bxBadge(eKey, '#2563eb', 'E', saveNew)}
                      {bigE
                        ? <textarea autoFocus style={{ flex: 1, minHeight: 100, fontSize: 12, resize: 'vertical', boxSizing: 'border-box' }} value={nl.zeile || ''} onChange={ev => setNl({ zeile: ev.target.value })} placeholder="Handlungsempfehlung" />
                        : <input style={{ flex: 1, fontSize: 12 }} value={nl.zeile || ''} onChange={ev => setNl({ zeile: ev.target.value })} placeholder="Handlungsempfehlung" />}
                    </div>
                    <input value={nl.stuetzt_sich_auf || ''} onChange={ev => setNl({ stuetzt_sich_auf: ev.target.value })}
                      placeholder="stützt sich auf (bx): z.B. b08, b16 (−b10 widerspricht)" style={{ width: '100%', fontSize: 12, padding: '3px 6px', marginBottom: 4, boxSizing: 'border-box' }} />
                    <textarea value={nl.herleitung || ''} onChange={ev => setNl({ herleitung: ev.target.value })}
                      placeholder="Herleitung — warum aus diesen Erkenntnissen dieses E folgt (inkl. Umgang mit dem Widerspruch)" style={{ width: '100%', minHeight: 48, fontSize: 12, padding: '3px 6px', marginBottom: 4, boxSizing: 'border-box', resize: 'vertical' }} />
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#0891b2', margin: '2px 0 3px' }}>P — Erwartung (nächste Woche prüfbar):</div>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                      <input value={nl.p_kosten || ''} onChange={ev => setNl({ p_kosten: ev.target.value })} placeholder="Δ Kosten (z.B. −28 €/Wo)" style={{ flex: '1 1 120px', fontSize: 12, padding: '3px 6px', boxSizing: 'border-box' }} />
                      <input value={nl.p_cpa || ''} onChange={ev => setNl({ p_cpa: ev.target.value })} placeholder="Kosten/Conv (z.B. →36 €)" style={{ flex: '1 1 120px', fontSize: 12, padding: '3px 6px', boxSizing: 'border-box' }} />
                      <select value={nl.p_conv || ''} onChange={ev => setNl({ p_conv: ev.target.value })} style={{ flex: '1 1 90px', fontSize: 12, padding: '3px 6px' }}>
                        <option value="">Conv …</option><option value="mehr">mehr</option><option value="gleich">gleich</option><option value="weniger">weniger</option>
                      </select>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: bigV ? 'flex-start' : 'center' }}>
                      {bxBadge(vKey, '#b45309', 'V', saveNew)}
                      {bigV
                        ? <textarea autoFocus style={{ flex: 1, minHeight: 80, fontSize: 12, color: '#b45309', resize: 'vertical', boxSizing: 'border-box' }} value={nl.verdikt || ''} onChange={ev => setNl({ verdikt: ev.target.value })} placeholder="Verdikt (i.d.R. nächste Woche)" />
                        : <input style={{ flex: 1, fontSize: 12, color: '#b45309' }} value={nl.verdikt || ''} onChange={ev => setNl({ verdikt: ev.target.value })} placeholder="Verdikt (i.d.R. nächste Woche)" />}
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>
    </section>
    );
  };

  const saveHb = async (typ, content, checked, quiet) => {
    patchHb(typ, quiet ? { busy: true } : { busy: true, msg: '' });
    try {
      const res = await fetch(`${API_BASE}/gads_hb.php`, { method: 'POST', headers: { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' }, body: JSON.stringify({ campaign: kCamp, week: clWeek, typ, content, checked }) });
      const j = await res.json();
      patchHb(typ, quiet ? { busy: false } : { busy: false, msg: j.success ? '✔ gespeichert' : ('Fehler: ' + (j.message || '')) });
    } catch (e) { patchHb(typ, quiet ? { busy: false } : { busy: false, msg: 'Fehler: ' + e }); }
  };
  const hbToggle = (typ, line) => {
    const cur = hb[typ];
    const next = cur.checked.includes(line) ? cur.checked.filter(l => l !== line) : [...cur.checked, line];
    patchHb(typ, { checked: next }); saveHb(typ, cur.content, next, true);
  };
  const renderHbPlan = (typ, nr, title, hint) => {
    const st = hb[typ];
    const zeilenAll = (st.struktur || []).flatMap(k => k.zeilen);
    const nTotal = zeilenAll.length;
    const nDone = zeilenAll.filter(z => z.erledigt_am).length;
    return (
      <section className="gk-section">
        <div className="gk-head" style={{ cursor: 'default' }}>
          <span>{title} <small style={{ fontWeight: 400 }}>({kCamp} × gewählte Zeiträume — {hint})</small></span>
          <span className="gx-frtop">
            <span className="gk-toolbar-hint">{nDone}/{nTotal} erledigt</span>
            <button className="gk-kbtn" onClick={() => { if (st.edit) saveHb(typ, st.content, st.checked); patchHb(typ, { edit: !st.edit }); }} disabled={st.busy}>{st.edit ? '💾 Speichern & schließen' : '✎ Freitext bearbeiten'}</button>
            {st.msg && <span className="gx-frmsg">{st.msg}</span>}
          </span>
        </div>
        <div className="gx-frbody">
          {st.edit ? (
            <textarea className="gx-frta gx-hb-edit" rows={20} value={st.content} onChange={e => patchHb(typ, { content: e.target.value })}
              placeholder="(eine Zeile je Anweisung; Zeilen mit # = Überschriften)" />
          ) : (st.struktur || []).length === 0 ? (
            <span className="gk-placeholder">(lädt …)</span>
          ) : (
            <div className="gx-hb-list">
              {st.struktur.map(k => {
                const done = k.zeilen.filter(z => z.erledigt_am).length;
                return (
                  <div key={k.kat} style={{ marginBottom: 4, opacity: k.zeilen.length ? 1 : 0.4 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, borderBottom: '1px solid var(--gk-border,#e5e7eb)', padding: '4px 0 2px' }}>
                      <span style={{ display: 'inline-block', minWidth: 38, color: '#2563eb' }}>{k.kat}</span>{k.label}
                      {k.zeilen.length > 0 && <small style={{ fontWeight: 400, color: '#888' }}> · {done}/{k.zeilen.length}</small>}
                    </div>
                    {k.zeilen.map(z => (
                      <div key={z.id} style={{ padding: '3px 0 3px 38px', fontSize: 13 }}>
                        <div className={z.erledigt_am ? 'gx-hb-done' : ''}>
                          <span style={{ color: z.erledigt_am ? '#16a34a' : '#bbb' }}>{z.erledigt_am ? '✅' : '○'}</span> {z.nummer}. {(z.zeile || '').replace(/^\d+\.\s*/, '')}
                          {z.warum_bx && <em style={{ color: '#888', fontStyle: 'normal' }}> (← {z.warum_bx})</em>}
                        </div>
                        {z.prognose && <div style={{ color: '#0891b2', fontSize: 12, paddingLeft: 20 }}>P: {z.prognose}</div>}
                        {z.verdikt && <div style={{ color: '#b45309', fontSize: 12, paddingLeft: 20 }}>V: {z.verdikt}</div>}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    );
  };

  // Journey/Profil-Vorschau (was geht an CD) — für den gewählten Zeitraum
  const jrPreview = async () => {
    const wks = picks(selWeeks); if (!wks.length) { setJrPrev(null); return; }
    const sorted = [...wks].sort();
    const startDate = sorted[0];
    const endDate = new Date(new Date(sorted[sorted.length - 1]).getTime() + 6 * 864e5).toISOString().slice(0, 10);
    const base = `startDate=${startDate}&endDate=${endDate}&campaign=${encodeURIComponent(SHORT2NAME[kCamp])}`;
    setJrBusy(true);
    try {
      const [sek, prof] = await Promise.all([
        fetch(`${API_BASE}/uje_export_csv.php?grain=sections&${base}`, { headers: { Authorization: 'Bearer ' + token() } }).then(r => r.text()),
        fetch(`${API_BASE}/uje_export_csv.php?grain=profil&${base}`, { headers: { Authorization: 'Bearer ' + token() } }).then(r => r.text()),
      ]);
      const sl = sek.trim().split('\n'), pl = prof.trim().split('\n');
      setJrPrev({ sekHead: sl[0] || '', sekBody: sl.slice(1, 9), sekN: Math.max(0, sl.length - 1), profHead: pl[0] || '', profBody: pl.slice(1, 9), profN: Math.max(0, pl.length - 1) });
    } catch (e) { setJrPrev(null); }
    setJrBusy(false);
  };

  const loadQuestions = useCallback((camp) => {
    setFrMsg('');
    fetch(`${API_BASE}/gads_kpi_questions.php?campaign=${camp}`, { headers: { Authorization: 'Bearer ' + token() } })
      .then(r => r.json())
      .then(j => { if (j.success) setFrText(Object.fromEntries(Object.entries(j.questions).map(([k, arr]) => [k, arr.join('\n')]))); })
      .catch(() => {});
  }, []);
  useEffect(() => { loadQuestions(kCamp); }, [kCamp, loadQuestions]);

  const saveQuestions = async () => {
    setFrBusy(true); setFrMsg('');
    try {
      const res = await fetch(`${API_BASE}/gads_kpi_questions.php`, {
        method: 'POST', headers: { Authorization: 'Bearer ' + token(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaign: kCamp, questions: frText }),
      });
      const j = await res.json();
      setFrMsg(j.success ? `✔ ${kCamp}: ${j.saved} Fragen gespeichert` : ('Fehler: ' + (j.message || '')));
    } catch (e) { setFrMsg('Fehler: ' + e); }
    setFrBusy(false);
  };

  const toggle = (setter) => (key) => setter(s => ({ ...s, [key]: !s[key] }));
  const setAllR = v => setSelR(Object.fromEntries(REPORTS.map(r => [r[0], v])));
  const picks = obj => Object.keys(obj).filter(k => obj[k]);

  const doExport = async () => {
    const wks = picks(selWeeks), reps = picks(selR), frs = picks(selFr);
    if (!reps.length && !frs.length && !inclCdhvK && !inclCdhvLP && !inclCl && !inclJourney && !inclLpMeta) { setErr('Nichts gewählt — Berichte, Fragen, HV-K/HV-LP, K-Änderungen, Journey oder LP-Stammdaten.'); return; }
    if ((reps.length || inclCdhvK || inclCdhvLP || inclCl || inclJourney) && !wks.length) { setErr('Für Berichte / HV-K/HV-LP / K-Änderungen / Journey mindestens 1 Woche im Zeitraum wählen.'); return; }
    setErr(''); setMsg(''); setBusy(true);
    try {
      // LP-Fragen a1–a11 (im Arbeitsbereich LP gepflegt) immer mitschicken → landen in <K>_Fragen.csv
      const fragenAll = [...frs, 'a1', 'a2', 'a3', 'a4', 'a5', 'a6', 'a7', 'a8', 'a9', 'a10', 'a11'].join(',');
      const p = new URLSearchParams({ campaigns: kCamp, weeks: wks.join(','), reports: reps.join(','), fragen: fragenAll, cdhvk: inclCdhvK ? '1' : '', cdhvlp: inclCdhvLP ? '1' : '', changelog: inclCl ? '1' : '', journey: inclJourney ? '1' : '', lpmeta: inclLpMeta ? '1' : '' });
      const res = await fetch(`${API_BASE}/gads_export_bxx.php?${p}`, { headers: { Authorization: 'Bearer ' + token() } });
      if (!res.ok) { const t = await res.text(); throw new Error('HTTP ' + res.status + ' ' + t.slice(0, 120)); }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const fname = (cd.match(/filename="([^"]+)"/) || [])[1] || 'GAds_Export.zip';
      const nf = res.headers.get('X-Files'), nr = res.headers.get('X-Rows');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob); a.download = fname;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
      setMsg(`✔ ${fname} — ${nf || '?'} Dateien · ${nr || '?'} Zeilen`);
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(false);
  };

  const chk = (on) => 'gk-kchk' + (on ? ' on' : '');
  const kFull = (KAMPS.find(k => k[0] === kCamp) || [kCamp, ''])[1];

  return (
    <div className="gk-wrap">
      <Link to="/admin" className="gk-back">← Zurück zum Dashboard</Link>
      <h2 className="gk-title">⬇ GADS Export <small>eine Kampagne · Wochen · Berichte · Fragen → ZIP (CSV)</small></h2>

      {/* Kampagne + Zeitraum ganz oben — gelten für ALLE CSVs */}
      <div className="gx-kbar">
        <span className="gx-kbar-lbl">Kampagne</span>
        <select className="gx-select gx-kbar-sel" value={kCamp} onChange={e => setKCamp(e.target.value)}>
          {KAMPS.map(([c, full]) => <option key={c} value={c}>{c} · {full}</option>)}
        </select>
        <span className="gx-kbar-lbl" style={{ marginLeft: 14 }}>Zeitraum <small style={{ fontWeight: 400, color: '#94a3b8' }}>(fix: die letzten 2 KW)</small></span>
        <div className="gx-kbar-weeks">
          {!weeks ? <span className="gk-placeholder">lädt…</span> : weeks.slice(0, 2).map((w, i) => (
            <label key={w.from} className={chk(true)} style={{ opacity: 0.65, cursor: 'default' }} title="Fest — die letzten zwei abgeschlossenen KW. Vorwoche = Entscheidungs-Woche, Vorvorwoche = Vergleich."><input type="checkbox" checked readOnly disabled /><span>{w.label} <small>· {i === 0 ? 'Entscheidung' : 'Vergleich'}</small></span></label>
          ))}
        </div>
      </div>
      <div className="gk-toolbar-hint" style={{ margin: '-8px 0 14px' }}>Kampagne gilt für <strong>alle</strong> CSVs. Der Zeitraum ist <strong>fest auf die letzten zwei KW</strong> (Vorwoche + Vorvorwoche) — so dreht sich das Rad lückenlos, kein Überspringen. (Alt-Wochen ansehen: eigene Lese-Ansicht folgt.)</div>

      <section className="gk-section">
        <div className="gk-head" style={{ cursor: 'default' }}><span>Berichte</span>
          <span><button className="gk-kbtn" onClick={() => setAllR(true)}>Alle an</button> <button className="gk-kbtn" onClick={() => setAllR(false)}>Alle ab</button></span>
        </div>
        <div className="gx-body">
          {REPORTS.map(([k, disp, lbl]) => (
            <label key={k} className={chk(selR[k])}><input type="checkbox" checked={!!selR[k]} onChange={() => toggle(setSelR)(k)} /><span>{disp} <small>{lbl}</small></span></label>
          ))}
        </div>
      </section>

      {/* 2 · Fragen — Anzeige entfernt (2026-07): Fragen sind jetzt im Arbeitsbereich K/LP je bx/a zugeordnet & editierbar.
          State (frText/selFr/loadQuestions) bleibt: speist die Panels im Arbeitsbereich + Fragen-CSV im ZIP. */}

      <section className="gk-section">
        <div className="gk-head" style={{ cursor: 'default' }}>
          <span>Handlungsempfehlungen Vorwoche K <small style={{ fontWeight: 400 }}>(HV-K — CDs K-Empfehlungen der Vorwoche, 1:1 wie im HP-K, mit Umsetzungs-Datum)</small></span>
        </div>
        <div className="gx-frbody">
          <div className="gx-frhint">
            <label className="gx-clincl"><input type="checkbox" checked={inclCdhvK} onChange={() => setInclCdhvK(v => !v)} /> <strong>HV-K als <code>{kCamp}_CD-HV-K.csv</code> ins ZIP</strong> (für die gewählten Wochen)</label>
            <div>Die in der Vorwoche <strong>erledigten</strong> K-Empfehlungen, Text 1:1 wie im Handlungsplan K, mit <strong>Datum + Uhrzeit</strong> der Umsetzung.</div>
          </div>
          {clHbPrev.K.length > 0 ? (
            <div className="gx-clrow">
              <label className="gx-cllbl">Vorschau <small>({clHbPrev.K.length} erledigte K-Empfehlungen)</small></label>
              <div className="gx-imp-items">{clHbPrev.K.map((u, i) => <div key={'k' + i}>✅ <span style={{ opacity: 0.65 }}>{(u.erledigt_am || '').slice(0, 16)}</span> · {(u.zeile || '').replace(/^\d+\.\s*/, '')}</div>)}</div>
            </div>
          ) : <div className="gx-frhint" style={{ opacity: 0.7 }}>(keine erledigten K-Empfehlungen für die Vorwoche der gewählten Woche)</div>}
        </div>
      </section>

      <section className="gk-section">
        <div className="gk-head" style={{ cursor: 'default' }}>
          <span>Handlungsempfehlungen Vorwoche LP <small style={{ fontWeight: 400 }}>(HV-LP — CDs LP-Empfehlungen der Vorwoche, 1:1 wie im HP-LP, mit Umsetzungs-Datum)</small></span>
        </div>
        <div className="gx-frbody">
          <div className="gx-frhint">
            <label className="gx-clincl"><input type="checkbox" checked={inclCdhvLP} onChange={() => setInclCdhvLP(v => !v)} /> <strong>HV-LP als <code>{kCamp}_CD-HV-LP.csv</code> ins ZIP</strong> (für die gewählten Wochen)</label>
            <div>Die in der Vorwoche <strong>erledigten</strong> LP-Empfehlungen, Text 1:1 wie im Handlungsplan LP, mit <strong>Datum + Uhrzeit</strong> der Umsetzung.</div>
          </div>
          {clHbPrev.LP.length > 0 ? (
            <div className="gx-clrow">
              <label className="gx-cllbl">Vorschau <small>({clHbPrev.LP.length} erledigte LP-Empfehlungen)</small></label>
              <div className="gx-imp-items">{clHbPrev.LP.map((u, i) => <div key={'l' + i}>✅ <span style={{ opacity: 0.65 }}>{(u.erledigt_am || '').slice(0, 16)}</span> · {(u.zeile || '').replace(/^\d+\.\s*/, '')}</div>)}</div>
            </div>
          ) : <div className="gx-frhint" style={{ opacity: 0.7 }}>(keine erledigten LP-Empfehlungen für die Vorwoche der gewählten Woche)</div>}
        </div>
      </section>

      {/* Journey + Profil: Sektion hier entfernt (2026-07-19) — die Journey liegt im Arbeitsbereich (b16-Flow, unten). Journey-CSVs bleiben per Default (inclJourney) im ZIP. */}

      {/* 6 · LP-Stammdaten — verschoben in den Arbeitsbereich LP (Bereich 9, oben als Basis).
          Der ZIP-Zusatz {kCamp}_LP_Sektionen.csv (a1–a11 Überschrift+Text aus LP-HTML) bleibt unberührt am Export gekoppelt. */}

      {/* CD-Import: Sektion hier entfernt (2026-07-19, Georg — „fehl am Platz"). Neuer Platz noch offen; Import-Handler (hpPreview/hpImport) + Endpoint gads_hp_import.php bleiben bestehen. */}

      {renderBxPlan(8, 'Arbeitsbereich K', 'K', 'Roh-Daten je bx + Entscheidung (E/P/V)')}
      {renderBxPlan(9, 'Arbeitsbereich LP', 'LP', 'LP-Section-Inhalt je a1–a11 + Entscheidung (E/P/V)')}

      <section className="gk-section">
        <div className="gk-head" style={{ cursor: 'default' }}>
          <span>Regeln <small style={{ fontWeight: 400 }}>({kCamp} — bestätigte P=V-Muster · ab n≥3 + Mechanismus → Regel)</small></span>
        </div>
        <div className="gx-frbody">
          <div className="gx-frhint">Die Ausbeute des Rades: bestätigte Muster (a) aus dem Motor. Ein Kandidat wird zur <strong>Regel</strong> bei <strong>n≥3</strong> gleichen Bestätigungen <strong>+ Mechanismus</strong> (warum es wirkt). „+1" = erneut bestätigt.</div>
          {rules.length === 0 ? <div className="gk-placeholder">(noch keine — im Motor „★ Regel-Kandidat" bei P=V)</div> : rules.map(r => (
            <div key={r.id} style={{ border: '1px solid', borderColor: r.status === 'regel' ? '#86efac' : '#e2e8f0', background: r.status === 'regel' ? '#f0fdf4' : '#fff', borderRadius: 6, padding: '6px 8px', marginBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 3 }}>
                <span style={{ background: r.status === 'regel' ? '#16a34a' : '#94a3b8', color: '#fff', borderRadius: 4, padding: '0 6px', fontSize: 11, fontWeight: 700 }}>{r.status === 'regel' ? 'REGEL' : 'Kandidat'}</span>
                <span style={{ color: '#2563eb', fontWeight: 700 }}>{r.kategorie}</span>
                <span style={{ fontSize: 11, color: '#64748b' }}>n={r.n}</span>
                <button className="gk-kbtn" style={{ marginLeft: 'auto' }} title="Erneut bestätigt (+1)" onClick={() => bumpRule(r.id)}>+1</button>
                <button className="gk-kbtn" title="löschen" onClick={() => deleteRule(r.id)}>🗑</button>
              </div>
              <input style={{ width: '100%', fontSize: 12, marginBottom: 3, boxSizing: 'border-box' }} defaultValue={r.bedingung || ''} placeholder="Bedingung — wann gilt die Regel?" onBlur={e => e.target.value !== (r.bedingung || '') && updateRule(r.id, { bedingung: e.target.value })} />
              <input style={{ width: '100%', fontSize: 12, boxSizing: 'border-box' }} defaultValue={r.mechanismus || ''} placeholder="Mechanismus — warum es wirkt (Pflicht für Regel)" onBlur={e => e.target.value !== (r.mechanismus || '') && updateRule(r.id, { mechanismus: e.target.value })} />
            </div>
          ))}
        </div>
      </section>

      <div className="gx-actions">
        <button className="gk-kbtn gx-go" onClick={doExport} disabled={busy}>{busy ? '⏳ erstelle ZIP…' : `⬇ ZIP erstellen (${kCamp})`}</button>
        <span className="gk-toolbar-hint">{kCamp} · {picks(selWeeks).length} Wochen · {picks(selR).length} Berichte · {picks(selFr).length} Fragen{inclCdhvK ? ' · +HV-K' : ''}{inclCdhvLP ? ' · +HV-LP' : ''}{inclJourney ? ' · +Journey' : ''}{inclLpMeta ? ' · +LP-Stammdaten' : ''}</span>
      </div>
      {err && <div className="gk-err">Fehler: {err}</div>}
      {msg && <div className="gx-ok">{msg}</div>}
      <p className="gk-toolbar-hint" style={{ marginTop: 12 }}>Ein ZIP je Kampagne (oben gewählt) für den gewählten Zeitraum: Berichte + Fragen + Was-wurde-gemacht + Journey/Profil + LP-Stammdaten (Semikolon, dt. Komma, UTF-8).</p>
    </div>
  );
}
